import React, { useState } from 'react';
import { ChevronDown, ChevronRight, History, Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { formatLiberacaoDate } from '../../../utils/liberacoes';
import { UnitLiberadoHistory } from '../../units/UnitLiberadosSection';

/**
 * "Quem já esteve liberado?" on the Operacional unit frame.
 *
 * The frame answers who may come in NOW; the porteiro is also the person asked "but she came last
 * month, what happened?", and that had no answer on the screen they are standing in front of.
 *
 * Collapsed by default — the live list is what the frame is for — but always PRESENT, even at
 * zero: hidden, the porteiro cannot tell "nobody has ever been cleared here" from "this frame does
 * not show that", which is exactly how its absence was reported on the unit page.
 *
 * A row here never carries a gate instruction (interfonar, no horário): those are about a
 * clearance that applies, and this one is over. See LiberadosHistoricoPanel.test.tsx.
 */

export interface LiberadosHistoricoPanelProps {
  rows: UnitLiberadoHistory[];
  isLoading: boolean;
  onPersonClick: (personId: string) => void;
  lightTheme?: boolean;
  forceUpper?: boolean;
}

export const LiberadosHistoricoPanel: React.FC<LiberadosHistoricoPanelProps> = ({
  rows,
  isLoading,
  onPersonClick,
  lightTheme = false,
  forceUpper = false,
}) => {
  const [open, setOpen] = useState(false);

  // Rendered even at zero: hidden, the porteiro could not tell "nobody has ever been cleared
  // here" from "this frame does not show that". One collapsed line is cheap; an absent feature
  // costs a question nobody can answer.

  return (
    <div className="mb-3 flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors',
          lightTheme ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200',
        )}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <History className="h-3 w-3" />
        {/* Not just "Histórico": a unit already has a VISIT history, and this one is about
            clearances. */}
        Histórico de liberações
        <span
          className={cn(
            'text-[11px] font-bold px-1.5 py-0.5 rounded-full leading-none',
            lightTheme ? 'bg-gray-100 text-gray-500' : 'bg-gray-800 text-gray-400',
          )}
        >
          {isLoading ? '…' : rows.length}
        </span>
      </button>

      {open && (
        <div className="space-y-1 mt-1.5">
          {isLoading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className={cn('h-4 w-4 animate-spin', lightTheme ? 'text-gray-400' : 'text-gray-600')} />
            </div>
          ) : rows.length === 0 ? (
            <p className={cn('text-[11.5px] px-0.5 py-1', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
              Nenhuma liberação encerrada nesta unidade
            </p>
          ) : (
            rows.map((row, index) => (
              <button
                key={row.roleId ?? `${row.personId}-${index}`}
                onClick={() => onPersonClick(row.personId)}
                className={cn(
                  'flex items-center gap-2 w-full text-left rounded-lg px-2.5 py-1.5 border transition-colors',
                  lightTheme
                    ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    : 'bg-gray-950/60 border-gray-800 hover:bg-gray-900',
                )}
              >
                <div
                  className={cn(
                    'w-7 h-7 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center opacity-70',
                    lightTheme ? 'bg-gray-100' : 'bg-gray-800',
                  )}
                >
                  {row.photo ? (
                    <img src={getAbsoluteImageUrl(row.photo)} alt={row.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className={cn('text-[11px] font-semibold', lightTheme ? 'text-gray-500' : 'text-gray-400')}>
                      {row.name[0]}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span
                    style={forceUpper ? { textTransform: 'uppercase' } : undefined}
                    className={cn('text-xs font-semibold truncate', lightTheme ? 'text-gray-600' : 'text-gray-400')}
                  >
                    {row.name}
                  </span>
                  {row.endedAt && (
                    <span className={cn('text-[11px] truncate', lightTheme ? 'text-gray-400' : 'text-gray-500')}>
                      {formatLiberacaoDate(row.endedAt)}
                    </span>
                  )}
                </div>

                {/* Why it ended — the only badge a history row carries. */}
                <span
                  className={cn(
                    'text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0',
                    lightTheme
                      ? 'bg-gray-100 text-gray-500 border-gray-200'
                      : 'bg-gray-800 text-gray-400 border-gray-700',
                  )}
                >
                  {row.endedLabel}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LiberadosHistoricoPanel;
